import { formatOrdinalDate } from './format';
import {
  NON_MEMBER_GUEST_MEMBERSHIP_TYPE,
  MEMBERSHIP_CUSTOM_FIELD_TYPES,
  MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS,
  MEMBERSHIP_FORM_FIELD_DEFS,
  MEMBERSHIP_NATIVE_FIELD_KEYS,
  MEMBERSHIP_LABEL_MAX_LENGTH,
  MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH,
} from '../constants/membership';

export const isNonMemberGuestMembershipType = (value) => String(value || '').trim().toLowerCase() === NON_MEMBER_GUEST_MEMBERSHIP_TYPE.toLowerCase();

/* A membership the club gave for good. No renewal date, no reminder, no
   form to fill in again next season — see server/utils/membershipTypes.js,
   which matches the same words so the two ends agree. Matched loosely
   because "Honorary Life Member" means the same thing. */
export const isLifeMembershipType = (value) => String(value || '').trim().toLowerCase().includes('life member');

/* ---- Who may enter a competition -------------------------------------
   A competition can list the membership types it accepts. No list means no
   restriction, so competitions that predate the setting stay open to all.
   The server enforces the same rule in server/utils/membershipEligibility.js
   — these are here so the UI can say so before the player submits. */

export const normalizeAllowedMembershipTypes = (raw) => (Array.isArray(raw) ? raw : [])
  .map(v => String(v || '').trim())
  .filter(Boolean);

export const membershipTypeAllowedForComp = (comp, membershipType) => {
  const allowed = normalizeAllowedMembershipTypes(comp && comp.allowedMembershipTypes);
  if (!allowed.length) return true;
  const value = String(membershipType || '').trim().toLowerCase();
  if (!value) return false;
  return allowed.some(a => a.toLowerCase() === value);
};

export const canUserEnterComp = (comp, user) => membershipTypeAllowedForComp(comp, user && user.membershipType);

/* '' when the competition is open to everyone, so callers can use it as the
   test for whether there is anything to show. */
export const compMembershipRestrictionText = (comp) =>
  normalizeAllowedMembershipTypes(comp && comp.allowedMembershipTypes).join(', ');

/* Membership runs to the day before the renewal date, so a member whose
   renewal falls on 1 Sep is shown as expiring 31 Aug until that date passes. */
export const getMembershipRenewalStatus = (user) => {
  /* Checked before the date, not after: a life member who still carries an
     old due date from before they were made one is still a life member. */
  if (user && isLifeMembershipType(user.membershipType)) {
    return { active: true, isLife: true, date: null, isExpired: false, text: 'Life member — no renewal needed' };
  }
  if (!user || !user.membershipRenewalDueAt) return { active: false, isLife: false, date: null, isExpired: false, text: 'Not set' };
  const dueDate = new Date(user.membershipRenewalDueAt);
  if (Number.isNaN(dueDate.getTime())) return { active: false, isLife: false, date: null, isExpired: false, text: 'Not set' };
  const expiryDate = new Date(dueDate);
  expiryDate.setDate(expiryDate.getDate() - 1);
  const isExpired = new Date() > expiryDate;
  const displayDate = isExpired ? dueDate : expiryDate;
  return {
    active: true,
    isLife: false,
    date: displayDate,
    isExpired,
    text: isExpired
      ? `Membership renewal due ${formatOrdinalDate(dueDate)}`
      : `Membership expires ${formatOrdinalDate(expiryDate)}`,
  };
};

export const getMembershipStatusLabel = (user) => {
  if (!user) return 'Membership Confirmed';
  if (isNonMemberGuestMembershipType(user.membershipType)) return NON_MEMBER_GUEST_MEMBERSHIP_TYPE;
  /* Above the pending check on purpose: a life membership is not something
     the member has to complete anything for. */
  if (isLifeMembershipType(user.membershipType)) return 'Life Member';
  if (String(user.membershipApprovalStatus || '').toLowerCase() === 'pending') return 'Pending';
  /* Approval first, whatever the renewal date says. Every member gets a
     renewal date — the server backfills one from the club's renewal day, and
     the profile infers one when it is missing — so a date proves nothing.
     Checking it first is how members who never sent a form in were being
     shown as "Membership Confirmed". */
  if (!user.membershipApprovedAt) return 'No Membership Application';
  const dueDate = user.membershipRenewalDueAt ? new Date(user.membershipRenewalDueAt) : null;
  if (dueDate && !Number.isNaN(dueDate.getTime()) && new Date() >= dueDate) return 'Renewal Due';
  return 'Membership Confirmed';
};

export const normalizeMembershipTypePricing = (raw, allowedOptions = []) => {
  const src = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
  const allowed = new Set((Array.isArray(allowedOptions) ? allowedOptions : []).map(v => String(v || '').trim()).filter(Boolean));
  const next = {};
  Object.entries(src).forEach(([rawKey, rawValue]) => {
    const key = String(rawKey || '').trim();
    if (!key) return;
    if (allowed.size > 0 && !allowed.has(key)) return;
    if (rawValue === '' || rawValue === null || rawValue === undefined) return;
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    next[key] = Number(parsed.toFixed(2));
  });
  return next;
};

export const formatMembershipTypeOptionLabel = (option, pricingMap) => {
  const label = String(option || '').trim();
  if (!label) return '';
  const price = pricingMap && pricingMap[label];
  if (price === undefined || price === null || price === '') return label;
  const parsed = Number(price);
  if (!Number.isFinite(parsed) || parsed < 0) return label;
  return `${label} - $${parsed.toFixed(2)}/year`;
};

export const sanitizeMembershipFieldKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9_]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 50);

export const sanitizeMembershipLabelText = (value) => String(value === undefined || value === null ? '' : value).slice(0, MEMBERSHIP_LABEL_MAX_LENGTH);

export const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);

export const sanitizeMembershipCustomType = (value) => {
  const type = String(value || '').toLowerCase().trim();
  return MEMBERSHIP_CUSTOM_FIELD_TYPES.includes(type) ? type : 'string';
};

export const sanitizeMembershipCustomOptions = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map(v => String(v || '').trim().slice(0, 80))
    .filter(Boolean)
    .filter((v, i, arr) => arr.findIndex(x => x.toLowerCase() === v.toLowerCase()) === i)
    .slice(0, 40);
};

export const buildDefaultMembershipFormConfig = () => ({
  enabled: true,
  introText: 'Complete this form to apply for membership.',
  secretaryEmails: '',
  showClubEmailPublicly: true,
  fields: MEMBERSHIP_FORM_FIELD_DEFS.map(def => ({
    key: def.key,
    label: def.label,
    detail: '',
    type: def.type,
    optionsSource: def.optionsSource || null,
    /* A native select with a fixed list of its own — the age groups. */
    options: Array.isArray(def.options) ? [...def.options] : [],
    enabled: true,
    required: !!def.required,
    requiredOnRenewal: !!def.requiredOnRenewal,
    storage: def.storage,
    personal: !!def.personal,
  })),
});

export const getMembershipFieldRequiredForContext = (field, renewalMode = false) => {
  if (!field || field.type === 'content') return false;
  if (renewalMode) {
    if (field.requiredOnRenewal !== undefined && field.requiredOnRenewal !== null) {
      return !!field.requiredOnRenewal;
    }
    return !!field.required;
  }
  return !!field.required;
};

export const normalizeMembershipFormConfig = (raw) => {
  const base = buildDefaultMembershipFormConfig();
  const src = raw && typeof raw === 'object' ? raw : base;
  const srcFields = Array.isArray(src.fields) ? src.fields : base.fields;
  const allowedStorage = new Set(['platform', 'email-only']);

  const baseMappedFields = MEMBERSHIP_FORM_FIELD_DEFS.map(def => {
    const found = srcFields.find(f => f && f.key === def.key) || {};
    const isLockedProfileField = MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS.has(def.key);
    const storage = allowedStorage.has(found.storage) ? found.storage : def.storage;
    const hasLabel = hasOwn(found, 'label');
    return {
      key: def.key,
      label: isLockedProfileField ? def.label : sanitizeMembershipLabelText(hasLabel ? found.label : def.label),
      detail: '',
      type: def.type,
      optionsSource: def.optionsSource || null,
      options: Array.isArray(def.options) ? [...def.options] : [],
      enabled: isLockedProfileField ? true : (found.enabled === undefined ? true : !!found.enabled),
      required: isLockedProfileField ? true : (found.required === undefined ? !!def.required : !!found.required),
      requiredOnRenewal: isLockedProfileField
        ? !!def.requiredOnRenewal
        : (found.requiredOnRenewal === undefined ? !!def.requiredOnRenewal : !!found.requiredOnRenewal),
      storage: isLockedProfileField ? 'platform' : storage,
      personal: isLockedProfileField ? !!def.personal : (found.personal === undefined ? !!def.personal : !!found.personal),
      isCustom: false,
    };
  });

  const usedKeys = new Set(baseMappedFields.map(field => field.key));
  const customSource = [
    ...(Array.isArray(src.customFields) ? src.customFields : []),
    ...srcFields.filter(f => f && !MEMBERSHIP_NATIVE_FIELD_KEYS.has(String(f.key || ''))),
  ];
  const customFields = [];

  customSource.forEach((srcField) => {
    if (!srcField || customFields.length >= 30) return;
    const hasLabel = hasOwn(srcField, 'label');
    const label = sanitizeMembershipLabelText(hasLabel ? srcField.label : '').trim();
    const key = sanitizeMembershipFieldKey(srcField.key || label || `custom_${customFields.length + 1}`);
    if (!key || MEMBERSHIP_NATIVE_FIELD_KEYS.has(key) || usedKeys.has(key)) return;

    const type = sanitizeMembershipCustomType(srcField.type);
    const storage = type === 'content'
      ? 'email-only'
      : (allowedStorage.has(srcField.storage) ? srcField.storage : 'email-only');
    const detail = String(srcField.detail || '').slice(0, MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH);

    customFields.push({
      key,
      label: hasLabel ? label : `Custom Field ${customFields.length + 1}`,
      detail,
      type,
      optionsSource: null,
      options: type === 'select' ? sanitizeMembershipCustomOptions(srcField.options) : [],
      enabled: srcField.enabled === undefined ? true : !!srcField.enabled,
      required: type === 'content' ? false : (srcField.required === undefined ? false : !!srcField.required),
      requiredOnRenewal: type === 'content'
        ? false
        : (srcField.requiredOnRenewal === undefined ? false : !!srcField.requiredOnRenewal),
      storage,
      personal: type === 'content' ? false : (srcField.personal === undefined ? true : !!srcField.personal),
      isCustom: true,
    });
    usedKeys.add(key);
  });

  const orderedFields = [...baseMappedFields, ...customFields];
  const sourceOrder = new Map();
  srcFields.forEach((field, index) => {
    if (!field || !field.key) return;
    if (!sourceOrder.has(field.key)) sourceOrder.set(field.key, index);
  });
  orderedFields.sort((a, b) => {
    const ai = sourceOrder.has(a.key) ? sourceOrder.get(a.key) : Number.MAX_SAFE_INTEGER;
    const bi = sourceOrder.has(b.key) ? sourceOrder.get(b.key) : Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });

  return {
    enabled: src.enabled === undefined ? true : !!src.enabled,
    introText: String(src.introText || '').slice(0, MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH),
    secretaryEmails: String(src.secretaryEmails || '').slice(0, MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH),
    showClubEmailPublicly: src.showClubEmailPublicly === undefined ? true : !!src.showClubEmailPublicly,
    fields: orderedFields,
  };
};
